import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import Footer from "@/components/Footer";
import { Briefcase, Code, Megaphone, Rocket, MapPin, Clock, ArrowRight } from "lucide-react";
import NavigationHeader from "@/components/NavigationHeader";
import PageTransition from "@/components/PageTransition";
import { useInfinitePublishedJobs } from "@/hooks/jobs/useJobs";
import SEO from "@/components/SEO";
import HomeAuroraBackground from "@/components/HomeAuroraBackground";

// --- ANIMATION VARIANTS ---
const SLOW_EASE = [0.22, 1, 0.36, 1];
const HERO_EASE = [0.16, 1, 0.3, 1];

const fadeInUp = {
  hidden: { opacity: 0, y: 100, scale: 0.98 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 1.2, ease: SLOW_EASE },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.25, delayChildren: 0.1 },
  },
};

const heroTextContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.05,
    },
  },
};

const heroTextItem = {
  hidden: { opacity: 0, y: 56, filter: "blur(10px)" },
  show: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: {
      duration: 0.9,
      ease: HERO_EASE,
    },
  },
};

const headerSegments = [
  { text: "Where growth meets purpose.", gradient: "text-[#1A3A5C]" },
  {
    text: "Join our team.",
    gradient:
      "bg-gradient-to-r from-[#9ecc48] via-teal-400 to-blue-500 bg-clip-text text-transparent",
  },
];

const getIconByRoles = (roles) => {
  if (!roles || roles.length === 0) return Briefcase;
  const roleTitles = roles.map((r) => r.title?.toLowerCase()).join(" ");
  if (roleTitles.includes("programmer") || roleTitles.includes("developer")) return Code;
  if (roleTitles.includes("designer") || roleTitles.includes("ui")) return Megaphone;
  if (roleTitles.includes("marketing") || roleTitles.includes("growth")) return Rocket;
  return Briefcase;
};

const formatSalary = (salary) => {
  if (!salary) return null;
  const num = parseFloat(salary);
  if (isNaN(num)) return salary;
  return `₱${num.toLocaleString()}`;
};

const normalizeJobText = (value) => {
  if (!value || typeof value !== "string") return "";

  if (typeof window === "undefined") {
    return value
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  const parsed = new DOMParser().parseFromString(value, "text/html");
  const plainText = parsed.documentElement.textContent || "";
  return plainText.replace(/\s+/g, " ").trim();
};

const JOBS_PAGE_SIZE = 4;

const JobHiring = () => {
  const {
    data: jobsData,
    isPending,
    isError,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfinitePublishedJobs(JOBS_PAGE_SIZE);

  const jobs = useMemo(
    () => jobsData?.pages?.flatMap((page) => (Array.isArray(page?.data) ? page.data : [])) || [],
    [jobsData]
  );

  const isJobsNotFoundError =
    error?.response?.status === 404 || /status code\s*404/i.test(error?.message || "");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const handleShowMoreJobs = () => {
    if (!hasNextPage || isFetchingNextPage) {
      return;
    }

    fetchNextPage();
  };

  return (
    <PageTransition>
      <SEO page="jobs" />
      <div
        className="relative w-full overflow-hidden font-sans text-slate-900"
        style={{
          background: "linear-gradient(135deg, #dbeafe 0%, #e8f4ff 20%, #f0fdf4 60%, #dcfce7 100%)",
        }}>
        <HomeAuroraBackground
          showStars={false}
          showWavingLines={true}
          className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
        />
        {/* --- BACKGROUND ANIMATIONS --- */}
        <style>{`
          @keyframes pulse-glow {
            0%, 100% { opacity: 0.6; transform: translate(-50%, 0) scale(1); }
            50% { opacity: 0.9; transform: translate(-50%, 0) scale(1.05); }
          }
          @keyframes gradient-x {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          .animate-gradient-x {
            background-size: 200% 200%;
            animation: gradient-x 3s ease infinite;
          }
        `}</style>

        <NavigationHeader />

        <section className="relative z-10 w-full px-6 pt-32 pb-20 md:px-12 md:pt-40 md:pb-32">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
            className="relative mx-auto max-w-400">
            {/* --- HEADER --- */}
            <motion.div
              variants={heroTextContainer}
              initial="hidden"
              animate="show"
              className="relative mx-auto mb-16 flex max-w-5xl flex-col items-center text-center md:mb-20">
              <motion.span
                variants={heroTextItem}
                className="mb-6 inline-flex items-center gap-2 self-start rounded-full border border-emerald-500/20 bg-emerald-500/10 px-5 py-2 text-sm font-medium text-emerald-800 shadow-sm backdrop-blur-sm transition-colors hover:bg-emerald-500/20 md:mb-8">
                <Briefcase className="h-4 w-4 text-emerald-500" />
                CAREERS
              </motion.span>

              <motion.h1
                variants={heroTextItem}
                style={{ perspective: "1000px" }}
                className="flex flex-col gap-0 text-4xl leading-[1.1] font-bold tracking-tighter md:text-6xl lg:text-[4.5rem]">
                {headerSegments.map((seg, i) => (
                  <div key={i} className="-mb-8 overflow-hidden pb-8">
                    <motion.span variants={heroTextItem} className={`block pb-2 ${seg.gradient}`}>
                      {seg.text}
                    </motion.span>
                  </div>
                ))}
              </motion.h1>
              <motion.p
                variants={heroTextItem}
                className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed font-light text-slate-700 md:text-xl">
                Explore our open roles and find your place in our growing team.
              </motion.p>
            </motion.div>

            {/* --- ENHANCED JOB CARDS GRID --- */}
            <div>
              {isPending ? (
                <div className="flex min-h-[400px] items-center justify-center">
                  <div className="flex flex-col items-center gap-4">
                    <div className="h-12 w-12 animate-spin rounded-full border-4 border-slate-700 border-t-emerald-500"></div>
                    <p className="text-slate-700">Loading job openings...</p>
                  </div>
                </div>
              ) : isError ? (
                <div className="flex min-h-100 items-center justify-center">
                  <div className="text-center">
                    <h3 className="mb-2 text-2xl font-bold text-slate-900">
                      {isJobsNotFoundError ? "No openings at the moment" : "We are preparing roles"}
                    </h3>
                    <p className="text-slate-400">
                      {isJobsNotFoundError
                        ? "Great things are coming. Please check back soon for new opportunities."
                        : "We are refreshing this page. Please try again in a moment."}
                    </p>
                  </div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="flex min-h-100 items-center justify-center">
                  <div className="text-center">
                    <h3 className="mb-2 text-2xl font-bold text-slate-900">
                      Please try again later
                    </h3>
                    <p className="text-slate-400">
                      We don`t have active career openings right now. Please check back soon
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
                    {jobs.map((job, index) => {
                      const JobIcon = getIconByRoles(job.roles);
                      const jobDetailsUrl = job.slug
                        ? `https://endsofttech.com/job-details/${encodeURIComponent(job.slug)}`
                        : "#";
                      const jobBannerImage =
                        [job.attachment_url, job.banner_image, job.cover_image, job.image].find(
                          (url) => typeof url === "string" && url.trim() !== ""
                        ) ||
                        "https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80";
                      const subtitleText = normalizeJobText(job.subtitle);
                      const requirements = Array.isArray(job.requirements)
                        ? job.requirements.map(normalizeJobText).filter(Boolean)
                        : [];
                      const benefits = Array.isArray(job.benefits)
                        ? job.benefits.map(normalizeJobText).filter(Boolean)
                        : [];

                      return (
                        <motion.a
                          variants={fadeInUp}
                          key={job.id || index}
                          href={jobDetailsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => {
                            if (!job.slug) e.preventDefault();
                          }}
                          className="h-full cursor-pointer">
                          <div className="group relative flex h-full flex-col overflow-hidden rounded-[2rem] border border-white/10 bg-slate-900 shadow-2xl backdrop-blur-xl transition-all duration-500 ease-out hover:-translate-y-1 hover:border-white/20 hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)]">
                            {/* Header Banner Image */}
                            <div className="relative w-full overflow-hidden rounded-t-[2rem]">
                              <img
                                src={jobBannerImage}
                                alt={job.title}
                                className="h-auto w-full object-contain transition-transform duration-700 group-hover:scale-105"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
                              <span className="absolute top-4 right-4 rounded-full border border-white/20 bg-slate-900/80 px-4 py-1.5 text-[11px] font-bold tracking-wider text-slate-200 uppercase shadow-sm backdrop-blur-md">
                                {job.company_name || job.badge || "ENDSOFTTECH WEB SOLUTIONS"}
                              </span>
                            </div>

                            {/* Icon */}
                            <div className="relative px-6 sm:px-8">
                              <div className="-mt-8 mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-[#9ecc48] to-[#7cb342] text-white shadow-[0_0_15px_rgba(158,204,72,0.4)] ring-4 ring-slate-900 transition-transform duration-300 group-hover:scale-110">
                                <JobIcon size={28} />
                              </div>
                            </div>

                            {/* Content Area */}
                            <div className="flex flex-grow flex-col px-6 pb-6 sm:px-8 sm:pb-7">
                              <h3 className="mb-2 text-xl leading-tight font-bold text-white transition-colors duration-300 group-hover:text-[#9ecc48] sm:text-2xl">
                                {job.title}
                              </h3>
                              {subtitleText && (
                                <p className="mb-5 line-clamp-2 text-sm leading-relaxed text-slate-200">
                                  {subtitleText}
                                </p>
                              )}

                              {/* Meta info */}
                              <div className="mb-5 flex flex-wrap gap-3 text-xs font-medium">
                                {job.location && (
                                  <div className="flex items-center gap-1.5 rounded-lg bg-slate-800/60 px-3 py-1.5 text-slate-300">
                                    <MapPin size={13} className="text-emerald-400" />
                                    {job.location}
                                  </div>
                                )}
                                {job.internship_hours && (
                                  <div className="flex items-center gap-1.5 rounded-lg bg-slate-800/60 px-3 py-1.5 text-slate-300">
                                    <Clock size={13} className="text-blue-400" />
                                    {job.internship_hours} hrs
                                  </div>
                                )}
                                {job.expected_salary && (
                                  <div className="flex items-center gap-1.5 rounded-lg bg-slate-800/60 px-3 py-1.5 text-slate-300">
                                    <span className="text-[10px] font-bold text-green-400">₱</span>
                                    {formatSalary(job.expected_salary)}
                                  </div>
                                )}
                              </div>

                              {/* Roles */}
                              {job.roles && job.roles.length > 0 && (
                                <div className="mb-5">
                                  <p className="mb-2 text-[11px] font-bold tracking-widest text-slate-300 uppercase">
                                    Open Roles
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {job.roles.map((role, i) => (
                                      <div key={i} className="flex flex-wrap items-center gap-1.5">
                                        <span className="rounded-md border border-blue-300/30 bg-blue-400/25 px-2.5 py-1 text-xs font-semibold text-blue-100">
                                          {role.title}
                                        </span>
                                        {role.tags?.map((tag, j) => (
                                          <span
                                            key={j}
                                            className="rounded-md border border-[#9ecc48]/35 bg-[#9ecc48]/25 px-2 py-1 text-[11px] font-medium text-[#d7f7a3]">
                                            {tag}
                                          </span>
                                        ))}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Requirements */}
                              {requirements.length > 0 && (
                                <div className="mb-5">
                                  <p className="mb-2 text-[11px] font-bold tracking-widest text-slate-300 uppercase">
                                    Requirements
                                  </p>
                                  <ul className="space-y-1">
                                    {requirements.slice(0, 3).map((req, i) => (
                                      <li
                                        key={i}
                                        className="flex items-start gap-2 text-xs text-slate-100">
                                        <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-[#9ecc48]" />
                                        {req}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}

                              {/* Benefits */}
                              {benefits.length > 0 && (
                                <div className="mb-6">
                                  <p className="mb-2 text-[11px] font-bold tracking-widest text-slate-300 uppercase">
                                    Benefits
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {benefits.map((benefit, i) => (
                                      <span
                                        key={i}
                                        className="rounded-full border border-emerald-300/30 bg-emerald-400/25 px-3 py-1 text-xs font-medium text-emerald-100">
                                        {benefit}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* CTA */}
                              <div className="mt-auto border-t border-white/10 pt-5">
                                <div className="group/btn flex w-full items-center justify-between py-1 text-sm font-bold text-slate-300 transition-colors hover:text-[#9ecc48]">
                                  <span>VIEW POSITION DETAILS</span>
                                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/10 bg-slate-800 transition-all duration-300 group-hover/btn:border-[#9ecc48]/50 group-hover/btn:bg-[#9ecc48]/20 group-hover/btn:shadow-[0_0_10px_rgba(158,204,72,0.3)]">
                                    <ArrowRight
                                      size={14}
                                      className="text-slate-400 transition-all duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:text-[#9ecc48]"
                                    />
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="absolute bottom-0 left-0 h-1 w-full bg-linear-to-r from-[#9ecc48] to-blue-500 opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
                          </div>
                        </motion.a>
                      );
                    })}
                  </div>

                  {hasNextPage && (
                    <div className="mt-10 flex justify-center">
                      <button
                        type="button"
                        onClick={handleShowMoreJobs}
                        disabled={isFetchingNextPage}
                        className="cursor-pointer rounded-full border border-[#295CAC]/30 px-6 py-2.5 font-medium text-[#295CAC] transition-colors hover:bg-[#295CAC]/10">
                        {isFetchingNextPage ? "Loading..." : "Show More"}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </section>

        <div className="relative z-10">
          <Footer />
        </div>
      </div>
    </PageTransition>
  );
};

export default JobHiring;
